import { zodResolver } from '@hookform/resolvers/zod';
import { UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useCreateAdminUser } from '../hooks/useCreateAdminUser';
import {
  createAdminUserSchema,
  type CreateAdminUserFormValues,
} from '../schemas/admin-user-schema';
import { useRoles } from '@/features/roles/hooks/useRoles';
import { ConfirmDialog } from '@/shared/components/ahkinia/ConfirmDialog/ConfirmDialog';
import { SelectInput, SelectItem } from '@/shared/components/ahkinia/Select/select';
import { FormControl, FormField, FormItem } from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { DialogFooter as ModalFooter, ModalForm } from '@/shared/components/ui/modal-form';
import { UnifiedButton } from '@/shared/components/ui/unified-button';
import { getRoleLabel } from '@/shared/utils/role-label.utils';

interface CreateAdminUserModalProps {
  open: boolean;
  onClose: () => void;
}

export function CreateAdminUserModal({ open, onClose }: CreateAdminUserModalProps) {
  const { mutate, isPending } = useCreateAdminUser();
  const { data: rolesData, isLoading: rolesLoading } = useRoles({ pageSize: 100 });
  const [pendingValues, setPendingValues] = useState<CreateAdminUserFormValues | null>(null);

  const form = useForm<CreateAdminUserFormValues>({
    resolver: zodResolver(createAdminUserSchema),
    defaultValues: {
      email: '',
      displayName: '',
      roleId: '',
    },
  });

  const onSubmit = (values: CreateAdminUserFormValues) => {
    setPendingValues(values);
  };

  const handleConfirm = () => {
    if (!pendingValues) return;
    mutate(pendingValues, {
      onSuccess: () => {
        setPendingValues(null);
        form.reset();
        onClose();
      },
      onError: () => {
        setPendingValues(null);
      },
    });
  };

  const handleClose = () => {
    if (!isPending) {
      form.reset();
      onClose();
    }
  };

  const selectedRoleName = getRoleLabel(
    rolesData?.items.find((r) => r.id === pendingValues?.roleId)?.name,
  );

  return (
    <>
      <ModalForm
        open={open}
        headerIcon={<UserPlus />}
        onOpenChange={handleClose}
        title="Agregar usuario"
        description="El nuevo usuario recibirá un correo con enlace para configurar su contraseña."
        className="sm:max-w-[45rem]"
        form={form}
        onSubmit={onSubmit}
        footer={
          <ModalFooter>
            <div className="flex flex-row justify-end w-full gap-4">
              <UnifiedButton
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isPending}
                data-testid="admin-users-create-cancel-button"
              >
                Cancelar
              </UnifiedButton>
              <UnifiedButton
                type="submit"
                variant="outline"
                actionType="submit"
                disabled={isPending}
                data-testid="admin-users-create-submit-button"
              >
                Agregar
              </UnifiedButton>
            </div>
          </ModalFooter>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[1.23rem]">
          <FormField
            control={form.control}
            name="displayName"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <Input
                    label="Nombre completo"
                    placeholder="Ingresar nombre"
                    autoComplete="off"
                    {...field}
                    disabled={isPending}
                    error={form.formState.errors.displayName?.message}
                    data-testid="admin-users-create-name-input"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <Input
                    label="Correo electrónico"
                    placeholder="Ingresar correo"
                    autoComplete="off"
                    {...field}
                    disabled={isPending}
                    error={form.formState.errors.email?.message}
                    data-testid="admin-users-create-email-input"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="roleId"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <SelectInput
                    label="Rol"
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={isPending || rolesLoading}
                    placeholder={rolesLoading ? 'Cargando roles...' : 'Seleccionar rol'}
                    error={form.formState.errors.roleId?.message}
                  >
                    {rolesData?.items.map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        {getRoleLabel(role.name)}
                      </SelectItem>
                    ))}
                  </SelectInput>
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </ModalForm>

      <ConfirmDialog
        open={!!pendingValues}
        onOpenChange={(v) => !isPending && !v && setPendingValues(null)}
        title="Confirmar creación"
        description={
          <span>
            Se creará el usuario{' '}
            <span className="font-medium text-light">{pendingValues?.email}</span> con nombre{' '}
            <span className="font-medium text-light">{pendingValues?.displayName}</span> y rol{' '}
            <span className="font-medium text-light">{selectedRoleName}</span>. Se enviará un correo
            de invitación.
          </span>
        }
        onConfirm={handleConfirm}
        confirmLabel="Confirmar"
        confirmLoadingLabel="Guardando..."
        isPending={isPending}
        onCancel={() => setPendingValues(null)}
      />
    </>
  );
}
